/**
 * Atlas Intelligence > Relationship Map: the network around one entity, the Browse rail's
 * catalog search, an entity's profile lists, and web research with the Atlas verdict
 * (src/aithora/v3/atlas/relmap.ts). Every answer carries `real`.
 *
 * Research rules (handoff "Web research + Atlas verdict"): sources come only from the
 * research service's own citation list, only public identifiers leave Aithora, the verdict
 * is labelled "Atlas verdict" and MODELED, and the server never sends it to Sales.
 */
import type { ApiClient } from './client';
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

/** One entity on the map, in the engine's own short keys. */
export interface RelNode {
  /** label */
  l: string;
  /** group: operator | building | payer | program | referral | pharmacy | staffing | ... */
  g: string;
  /** state (postal code) */
  co: string;
  role: string;
  /** headline metric label, value and unit ("$M/yr", "%", "transfers/yr", ...) */
  m: string;
  v: number;
  u: string;
  /** trend, % a year */
  t: number;
  beds?: number | null;
  /** occupancy, percent */
  occ?: number | null;
  /** county FIPS (Territory) when the back end knows it */
  fips?: string | null;
  /** CMS address point; the page finds the county from it when no FIPS is given */
  lat?: number | null;
  lng?: number | null;
  city?: string | null;
  /** the badge on this entity's figures: DATA, MODELED or MOCK */
  badge?: string;
  /** a note under an operator's profile (where ownership comes from) */
  ownNote?: string;
}

/** One link: supplier s to customer c, flow u ($M/yr), shares sr / cc (%), trend t. */
export interface RelEdge {
  s: string;
  c: string;
  sr: number;
  cc: number;
  b: string;
  u: number;
  t: number;
  /** true when the dollar flow is our estimate, not a reported figure */
  modeled?: boolean;
  /** where a sourced link comes from (e.g. "CMS ownership filing") */
  src?: string;
}

export interface RelGraph {
  real: boolean;
  focal: string;
  /** the tag next to the title (e.g. "CATALOG + MODELED", "MOCK DATA") */
  badge: string;
  /** the Browse rail's footer */
  footnote: string;
  /** the focus panel's source line */
  sourceNote: string;
  /** Territory's note on locations, and its legend badge */
  locNote: string;
  locBadge: string;
  nodes: Record<string, RelNode>;
  edges: RelEdge[];
  focalOpts: [string, string[]][];
  /** Disruption sim choices: [entity id, label] */
  disrupt: [string, string][];
  /** county FIPS -> label city */
  cities: Record<string, string>;
}

export interface RelHit {
  id: string;
  l: string;
  g: string;
  role: string;
  co: string;
  v: number;
  u: string;
  t: number;
}

export interface RelSearch {
  real: boolean;
  hits: RelHit[];
  nextCursor: string | null;
}

export interface RelProfileRow {
  id: string;
  name: string;
  sub: string;
  value: string;
  modeled?: boolean;
}

export interface RelEntity {
  real: boolean;
  id: string;
  kind: 'operator' | 'building' | 'payer' | 'program' | 'partner';
  name: string;
  role: string;
  state: string;
  /** operator: buildings, payers, suppliers; building: operator, payers, referrals; payer: facilities */
  lists: Record<string, RelProfileRow[]>;
  totals: Record<string, number | null>;
  notes: string[];
  /** operator: more of its facilities on the next page (?page=). */
  more?: boolean;
}

export type ResearchEntityType = 'operator' | 'building' | 'payer' | 'program' | 'partner';
export type VerdictStance = 'pursue' | 'watch' | 'pass';

export interface AtlasVerdict {
  stance: VerdictStance;
  confidence: 'low' | 'medium' | 'high';
  line: string;
  reasons: { text: string; source: 'ours' | 'web' }[];
  recheck: { when: string; condition: string };
}

export interface EntityResearch {
  /** done (findings), empty (searched, nothing found), failed (nothing saved, not charged) */
  state: 'done' | 'empty' | 'failed';
  ranAt: string;
  /** the badge on the research block (MOCK in the pixel fixture only) */
  badge?: string;
  findings: { text: string; citations: number[]; ours: boolean }[];
  sources: { title: string; site: string; url: string; date: string | null }[];
  /** Operators and buildings only, and never for Sales. MODELED. */
  verdict?: AtlasVerdict | null;
  error?: string;
  cached?: boolean;
}

export interface VerdictHistoryRow {
  date: string;
  stance: VerdictStance;
  outcome: 'right' | 'wrong' | 'open';
  note: string;
}

/** GET: the network around `focal` (default: the largest operator in the launch state). */
export const relmapGraph = route<RelGraph, { focal?: string }>()('GET', `${A}/intelligence/relmap`, { honest: true });
/** GET: Browse search over the whole catalog plus operators and payer programs. */
export const relmapSearch = route<RelSearch, { q: string; group?: string; cursor?: string; limit?: number }>()('GET', `${A}/intelligence/relmap/search`, { honest: true });
/** GET: one entity's profile lists. */
export const relmapEntity = route<RelEntity, { page?: number }>()('GET', `${A}/intelligence/relmap/entity/:id`, { honest: true });
/** GET: the stored research on an entity (null when never run). */
export const entityResearch = route<{ real: boolean; research: EntityResearch | null }>()('GET', `${A}/intelligence/research/:entityType/:id`, { honest: true });
/** GET: past verdicts on an entity, scored at their recheck date (not for Sales). */
export const verdictHistory = route<{ real: boolean; history: VerdictHistoryRow[] }>()('GET', `${A}/intelligence/verdicts/:id/history`, { honest: true });

/**
 * POST .../refresh: runs the research now. The server streams one JSON object per line:
 * {"step":"searching"}, {"step":"reading","n":5}, {"step":"writing"}, then
 * {"research": {...}} or {"error": "..."}. onStep gets each step.
 */
export async function refreshEntityResearch(
  client: ApiClient,
  entityType: ResearchEntityType,
  id: string,
  onStep?: (ev: { step: string; n?: number }) => void,
  signal?: AbortSignal,
): Promise<EntityResearch> {
  const res = await client.raw(`${A}/intelligence/research/${encodeURIComponent(entityType)}/${encodeURIComponent(id)}/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/x-ndjson' },
    body: '{}',
    signal,
  });
  if (!res.ok) {
    let msg = `Research could not run (${res.status}).`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch {
      /* not JSON */
    }
    throw Object.assign(new Error(msg), { status: res.status });
  }
  const handle = (line: string): EntityResearch | null => {
    if (!line.trim()) return null;
    let d: { step?: string; n?: number; research?: EntityResearch; error?: string };
    try {
      d = JSON.parse(line);
    } catch {
      return null;
    }
    if (d.step) onStep?.({ step: d.step, n: d.n });
    if (d.error) throw new Error(d.error);
    return d.research ?? null;
  };
  let out: EntityResearch | null = null;
  const reader = res.body?.getReader?.();
  if (reader) {
    const dec = new TextDecoder();
    let buf = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let i: number;
      while ((i = buf.indexOf('\n')) >= 0) {
        const r = handle(buf.slice(0, i));
        buf = buf.slice(i + 1);
        if (r) out = r;
      }
    }
    const r = handle(buf);
    if (r) out = r;
  } else {
    for (const line of (await res.text()).split('\n')) {
      const r = handle(line);
      if (r) out = r;
    }
  }
  if (!out) throw new Error('The research service sent no answer.');
  return out;
}
